import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Companies, CompaniesParams, Company } from '@/api/_schemas/companies'
import { useAuthContext } from '@/contexts/auth-context'
import { useCompaniesInfinite } from '@/hooks/use-companies'

type CompanyContextValues = {
  currentCompany: Company | null
  companiesInfinite: UseInfiniteQueryResult<InfiniteData<Companies>>
  companies: Company[]
  totalCompanies: number
  setCompaniesFilters: React.Dispatch<React.SetStateAction<CompaniesParams>>
  resetTotalCompanies: () => void
}

type CompanyContextProviderProps = PropsWithChildren

export const CompanyContext = createContext<CompanyContextValues>(
  {} as CompanyContextValues,
)

const CompanyContextProvider = ({ children }: CompanyContextProviderProps) => {
  const { profile } = useAuthContext()

  const [currentCompany, setCurrentCompany] = useState<Company | null>(null)
  const [totalCompanies, setTotalCompanies] = useState(0)
  const [filters, setFilters] = useState<CompaniesParams>({})

  const companiesInfinite = useCompaniesInfinite(filters)

  const companies = useMemo(
    () =>
      companiesInfinite.data
        ? companiesInfinite.data.pages.flatMap((cur) => cur.data)
        : [],
    [companiesInfinite.data],
  )

  const resetTotalCompanies = useCallback(() => {
    setTotalCompanies(0)
  }, [])

  useEffect(() => {
    setCurrentCompany(profile?.empresa ?? null)
  }, [profile?.empresa])

  useEffect(() => {
    setTotalCompanies(
      (prevValue) =>
        prevValue || (companiesInfinite.data?.pages[0].meta.total ?? 0),
    )
  }, [companiesInfinite.data])

  return (
    <CompanyContext.Provider
      value={{
        currentCompany,
        companiesInfinite,
        companies,
        totalCompanies,
        setCompaniesFilters: setFilters,
        resetTotalCompanies,
      }}
    >
      {children}
    </CompanyContext.Provider>
  )
}

function useCompanyContext() {
  return useContext(CompanyContext)
}

function useCompanyChange(callback: () => void) {
  const companyRef = useRef<number | null>(null)
  const { currentCompany } = useCompanyContext()

  useEffect(() => {
    if (
      companyRef.current &&
      currentCompany?.cod_empresa !== companyRef.current
    ) {
      callback()
    }

    companyRef.current = currentCompany?.cod_empresa ?? null
  }, [currentCompany?.cod_empresa, callback])
}

export { CompanyContextProvider, useCompanyContext, useCompanyChange }
